import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useLoneStarAuth } from '@/context/LoneStarAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lands here after an authorized Amigos manager clicks "Open Lone Star
 * Dashboard". Exchanges the single-use code+state (minted by
 * POST /api/sso/switch-request) for a brand-scoped Lone Star session —
 * mirrors an OIDC Authorization Code callback, just self-hosted since
 * both brand contexts currently live in one backend (see server.py notes).
 */
const SSOCallback = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useLoneStarAuth();
  const [status, setStatus] = useState('exchanging'); // exchanging | error
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return; // guard against double-invoke (StrictMode / re-render)
    ranRef.current = true;

    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) { setStatus('error'); return; }

    axios.post(`${API}/sso/exchange`, { code, state })
      .then((res) => {
        setSession(res.data.access_token, res.data.user);
        const dest = res.data.redirect_to && res.data.redirect_to.startsWith('/') ? res.data.redirect_to : '/lonestar/overview';
        navigate(dest, { replace: true });
      })
      .catch(() => setStatus('error'));
  }, [params, navigate, setSession]);

  return (
    <div className="amigos-app dash-body">
      <div className="dash-login">
        <div className="dash-login__card" style={{ textAlign: 'center' }} data-testid="sso-callback-card">
          {status === 'exchanging' ? (
            <>
              <Loader2 size={28} className="spinner" style={{ border: 'none', margin: '0 auto 1rem' }} />
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Completing secure sign-in to Lone Star Bonding…</p>
            </>
          ) : (
            <>
              <ShieldAlert size={28} color="var(--red-bright)" style={{ margin: '0 auto 1rem' }} />
              <p style={{ color: 'var(--text-primary)', fontWeight: 600 }} data-testid="sso-callback-error">This sign-in link is invalid or has expired.</p>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.4rem' }}>
                {'Please return to the Amigos dashboard and use "Open Lone Star Dashboard" again.'}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SSOCallback;
